/**
 * Help Center articles are stored as raw Markdown (body only).
 * Optional publisher front matter in .md files is stripped before save.
 */

export interface ParsedHelpCenterMarkdown {
  title?: string;
  moduleLabel?: string;
  coverHint?: string;
  body: string;
}

/** Strip markdown syntax for short list excerpts */
export function markdownToPlainText(content: string, maxLength = 160): string {
  const plain = content
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength).trim()}…`;
}

/**
 * Parse a help-center-articles/*.md file: keep body after ---, optional metadata above.
 */
export function parseHelpCenterMarkdownFile(raw: string): ParsedHelpCenterMarkdown {
  const normalized = raw.replace(/^\uFEFF/, "").trim();
  if (!normalized) {
    return { body: "" };
  }

  const lines = normalized.split(/\r?\n/);
  let title: string | undefined;
  let moduleLabel: string | undefined;
  let coverHint: string | undefined;
  let dividerIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "---") {
      dividerIndex = i;
      break;
    }

    const headingMatch = trimmed.match(/^#\s+(.+)$/);
    if (headingMatch) {
      title = headingMatch[1].trim();
      continue;
    }

    const moduleMatch = trimmed.match(
      /^\*\*Module\s*\(select in dropdown\):\*\*\s*(.+)$/i,
    );
    if (moduleMatch) {
      moduleLabel = moduleMatch[1].trim();
      continue;
    }

    const coverMatch = trimmed.match(/^\*\*Cover image:\*\*\s*(.+)$/i);
    if (coverMatch) {
      coverHint = coverMatch[1].trim();
      continue;
    }
  }

  const bodyStart = dividerIndex >= 0 ? dividerIndex + 1 : 0;
  let body = lines.slice(bodyStart).join("\n").trim();

  // No --- divider: if file only has metadata lines at top, still return rest as body
  if (dividerIndex < 0 && (title || moduleLabel || coverHint)) {
    const metaPatterns = [
      /^#\s+/,
      /^\*\*Module/i,
      /^\*\*Cover image/i,
      /^\*\*Suggested cover image/i,
    ];
    const contentLines = lines.filter(
      (line) => !metaPatterns.some((p) => p.test(line.trim())),
    );
    body = contentLines.join("\n").trim();
  }

  return { title, moduleLabel, coverHint, body };
}

/** Normalize content from API: use as-is (already stored markdown body) */
export function normalizeArticleMarkdownContent(content: string): string {
  if (!content?.trim()) return "";
  // Legacy saves that included full .md file — strip front matter on display
  if (content.includes("---")) {
    const { body } = parseHelpCenterMarkdownFile(content);
    if (body.trim()) return body;
  }
  return content;
}
